import { useCallback, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { BucketFileEntry, BucketSelectedFile, OtherAttachmentEntry, toBucketFileEntry } from "@RequestFormV2/sections/Provider/hooks/attachmentTypes";
import { ProviderOption, SelectedParcelAccount } from "@RequestFormV2/sections/Provider/types";
import { buildAllProviderSelections } from "@RequestFormV2/sections/Provider/utils/buildAllProviderSelections";
import { getProviderSelectionKey, isProviderEntrySelected } from "@RequestFormV2/sections/Provider/utils/getProviderSelectionKey";
import { isAllProvidersSelected } from "@RequestFormV2/sections/Provider/utils/providerSelection";
import { usePersistDraft } from "@RequestFormV2/hooks/usePersistDraft";
import { trackQuoteProviderSelectAll } from "@RequestFormV2/formEvents/requestFormAnalytics";

type UseProviderHandlersParams = {
  allProvidersRequired?: boolean;
};

export function useProviderHandlers({ allProvidersRequired = false }: UseProviderHandlersParams = {}) {
  const { getValues, setValue } = useFormContext();
  const persistDraft = usePersistDraft();
  const selectedProviders: string[] = useWatch({ name: "invited_providers.providers" }) ?? [];
  const selectedParcelAccounts: SelectedParcelAccount[] =
    useWatch({ name: "invited_providers.parcel_accounts" }) ?? [];

  const handleToggleProvider = useCallback(
    (provider: ProviderOption, hasParcelAccounts = false) => {
      const providers: string[] = getValues("invited_providers.providers") ?? [];
      const parcelAccounts: SelectedParcelAccount[] = getValues("invited_providers.parcel_accounts") ?? [];
      const key = getProviderSelectionKey(provider);

      if (isProviderEntrySelected(provider, providers)) {
        if (allProvidersRequired) {
          return;
        }
        setValue(
          "invited_providers.providers",
          providers.filter((entry) => !isProviderEntrySelected(provider, [entry])),
        );
        setValue(
          "invited_providers.parcel_accounts",
          parcelAccounts.filter((item) => !isProviderEntrySelected(provider, [item.provider_id])),
        );
        persistDraft("invited_providers");
        return;
      }

      if (hasParcelAccounts) {
        return;
      }

      setValue("invited_providers.providers", [...providers, key]);
      persistDraft("invited_providers");
    },
    [allProvidersRequired, getValues, persistDraft, setValue],
  );

  const handleParcelAccountChange = useCallback(
    (provider: ProviderOption, accountId: string | null) => {
      const key = getProviderSelectionKey(provider);
      let parcelAccounts: SelectedParcelAccount[] = (getValues("invited_providers.parcel_accounts") || []).slice();
      let providers: string[] = (getValues("invited_providers.providers") || []).slice();
      const existingParcelAccount = parcelAccounts.find((item) => isProviderEntrySelected(provider, [item.provider_id]));

      if (!accountId) {
        if (allProvidersRequired) {
          return;
        }
        parcelAccounts = parcelAccounts.filter((item) => !isProviderEntrySelected(provider, [item.provider_id]));
        if (isProviderEntrySelected(provider, providers)) {
          providers = providers.filter((entry) => !isProviderEntrySelected(provider, [entry]));
        }
      } else {
        const parcelAccountId = Number(accountId);
        if (existingParcelAccount) {
          parcelAccounts = parcelAccounts.map((item) =>
            isProviderEntrySelected(provider, [item.provider_id])
              ? { ...item, parcel_account_id: parcelAccountId, provider_id: key }
              : item,
          );
        } else {
          parcelAccounts.push({ parcel_account_id: parcelAccountId, provider_id: key });
        }

        if (!isProviderEntrySelected(provider, providers)) {
          providers.push(key);
        }
      }

      setValue("invited_providers.parcel_accounts", parcelAccounts);
      setValue("invited_providers.providers", providers);
      persistDraft("invited_providers");
    },
    [allProvidersRequired, getValues, persistDraft, setValue],
  );

  const handleSelectAll = useCallback(
    (providers: ProviderOption[]) => {
      const allSelected = isAllProvidersSelected(providers, selectedProviders, selectedParcelAccounts);

      if (allSelected) {
        if (allProvidersRequired) {
          return;
        }
        setValue("invited_providers.providers", []);
        setValue("invited_providers.parcel_accounts", []);
        persistDraft("invited_providers");
        return;
      }

      const { providers: allSelectedProviders, parcel_accounts: allSelectedParcelAccounts } =
        buildAllProviderSelections(providers);

      setValue("invited_providers.providers", allSelectedProviders);
      setValue("invited_providers.parcel_accounts", allSelectedParcelAccounts);
      persistDraft("invited_providers");
      trackQuoteProviderSelectAll(allSelectedProviders.length);
    },
    [allProvidersRequired, persistDraft, selectedParcelAccounts, selectedProviders, setValue],
  );

  const handleAddBucketFile = useCallback(
    (file: BucketSelectedFile, bucketName: string) => {
      const allFiles: BucketFileEntry[] = (getValues("bucket_attachments") || []).slice();
      const entry = toBucketFileEntry(file, bucketName);
      const idx = allFiles.findIndex((item) => item.id === file.id);
      if (idx >= 0) {
        allFiles[idx] = entry;
      } else {
        allFiles.push(entry);
      }
      setValue("bucket_attachments", allFiles);
      persistDraft("bucket_attachments");
    },
    [getValues, persistDraft, setValue],
  );

  const handleDeleteBucketFile = useCallback(
    (file: { id: number }) => {
      const allFiles: BucketFileEntry[] = (getValues("bucket_attachments") || []).slice();
      setValue("bucket_attachments", allFiles.filter((f) => f.id !== file.id));
      persistDraft("bucket_attachments");
    },
    [getValues, persistDraft, setValue],
  );

  const handleAppendBucketAttachments = useCallback(
    (entries: BucketFileEntry[]) => {
      const existing: BucketFileEntry[] = getValues("bucket_attachments") || [];
      setValue("bucket_attachments", [...existing, ...entries]);
      persistDraft("bucket_attachments");
    },
    [getValues, persistDraft, setValue],
  );

  const handleRemoveBucketFileByRef = useCallback(
    (fileRef: string) => {
      const existing: BucketFileEntry[] = getValues("bucket_attachments") || [];
      setValue(
        "bucket_attachments",
        existing.filter((f) => f.file !== fileRef),
      );
      persistDraft("bucket_attachments");
    },
    [getValues, persistDraft, setValue],
  );

  const handleAppendOtherAttachments = useCallback(
    (files: OtherAttachmentEntry[]) => {
      const existing: OtherAttachmentEntry[] = getValues("other_attachments") || [];
      setValue("other_attachments", [...existing, ...files]);
      persistDraft("other_attachments");
    },
    [getValues, persistDraft, setValue],
  );

  const handleRemoveOtherFile = useCallback(
    (fileId: number | string) => {
      const existing: OtherAttachmentEntry[] = getValues("other_attachments") || [];
      setValue("other_attachments", existing.filter((f) => f.id !== fileId));
      persistDraft("other_attachments");
    },
    [getValues, persistDraft, setValue],
  );

  return useMemo(
    () => ({
      selectedProviders,
      selectedParcelAccounts,
      handleToggleProvider,
      handleParcelAccountChange,
      handleSelectAll,
      handleAddBucketFile,
      handleDeleteBucketFile,
      handleAppendBucketAttachments,
      handleRemoveBucketFileByRef,
      handleAppendOtherAttachments,
      handleRemoveOtherFile,
    }),
    [
      selectedProviders,
      selectedParcelAccounts,
      handleToggleProvider,
      handleParcelAccountChange,
      handleSelectAll,
      handleAddBucketFile,
      handleDeleteBucketFile,
      handleAppendBucketAttachments,
      handleRemoveBucketFileByRef,
      handleAppendOtherAttachments,
      handleRemoveOtherFile,
    ],
  );
}
